import { __awaiter } from 'tslib';
import { getInstalledWallets, disconnect } from 'get-starknet';
import { Contract } from 'starknet';
import { StarknetChainId } from 'starknet/constants.js';
import { uint256ToBN } from 'starknet/dist/utils/uint256.js';
import { DynamicError } from '../../errors.js';
import { normalizeWalletName } from '../../utils/normalizeWalletName/normalizeWalletName.js';
import { logger } from '../../utils/logger.js';
import { formatTypedDataMessage } from '../../utils/convertors.js';
import { ETH_STARKNET_ADDRESS } from '../constants.js';
import ETH_CONTRACT_ABI from './ethereumContractAbi.js';

const ACCOUNT_CHANGED_EVENT_LISTENER = 'accountsChanged';
const NETWORK_CHANGED_EVENT_LISTENER = 'networkChanged';
class StarknetProvider {
    constructor(name, windowKey) {
        this.connectedChain = 'STARK';
        this.supportedChains = ['STARK'];
        this.canConnectViaQrCode = false;
        this.canConnectViaCustodialService = false;
        this.isWalletConnect = false;
        this.name = name;
        this.windowKey = windowKey;
    }
    get key() {
        return normalizeWalletName(this.name);
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.getWallet();
            try {
                if (wallet && !wallet.isConnected) {
                    yield wallet.enable({ showModal: false });
                }
            }
            catch (err) {
                logger.error(err);
            }
        });
    }
    fetchPublicAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.getWallet();
            yield this.connect();
            return Promise.resolve(wallet === null || wallet === void 0 ? void 0 : wallet.selectedAddress);
        });
    }
    getNetwork() {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined;
        });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.getWallet();
            return wallet === null || wallet === void 0 ? void 0 : wallet.account;
        });
    }
    getWallet() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.wallet) {
                const providers = yield getInstalledWallets();
                this.wallet = providers.find((provider) => provider.name === this.name);
            }
            return this.wallet;
        });
    }
    getWeb3Provider() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.getWallet();
            return wallet === null || wallet === void 0 ? void 0 : wallet.provider;
        });
    }
    getRpcProvider() {
        return __awaiter(this, void 0, void 0, function* () {
            return;
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.getWallet();
            const walletAddress = yield this.fetchPublicAddress();
            if (!walletAddress || !wallet) {
                logger.error('Could not fetch wallet address for signing message');
                return undefined;
            }
            const { account } = wallet;
            const chainId = account.chainId === StarknetChainId.TESTNET ? 'SN_GOERLI' : 'SN_MAIN';
            const encodedMessage = formatTypedDataMessage(messageToSign, chainId);
            const signature = yield (account === null || account === void 0 ? void 0 : account.signMessage(encodedMessage));
            return signature === null || signature === void 0 ? void 0 : signature.join(',');
        });
    }
    proveOwnership(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.signMessage(messageToSign);
        });
    }
    getBalance() {
        return __awaiter(this, void 0, void 0, function* () {
            const walletAddress = yield this.fetchPublicAddress();
            const provider = yield this.getWeb3Provider();
            if (!walletAddress || !provider) {
                logger.error('Could not fetch wallet address for getting balance');
                return undefined;
            }
            const contract = new Contract(ETH_CONTRACT_ABI, ETH_STARKNET_ADDRESS, provider);
            try {
                const { balance } = yield contract.balanceOf(walletAddress);
                const gweiBalance = parseInt(uint256ToBN(balance).toString(10));
                /**
                 * Dividing by 1e18 as the returned balance is a Gwei number.
                 * Read more here: https://www.investopedia.com/terms/g/gwei-ethereum.asp#toc-what-is-gwei
                 */
                return (gweiBalance / 1e18).toFixed(6);
            }
            catch (error) {
                throw new DynamicError('Something went wrong');
            }
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            disconnect({
                clearLastWallet: true,
            });
        });
    }
    getStarknetWindowObject() {
        const starknetWindowInstance = window[`starknet_${this.windowKey}`] || window['starknet'];
        if ((starknetWindowInstance === null || starknetWindowInstance === void 0 ? void 0 : starknetWindowInstance.name) === this.name) {
            return starknetWindowInstance;
        }
        else {
            return undefined;
        }
    }
    isInstalledOnBrowser() {
        var _a;
        const starknetWindowInstance = (_a = window[`starknet_${this.windowKey}`]) !== null && _a !== void 0 ? _a : window['starknet'];
        return (starknetWindowInstance === null || starknetWindowInstance === void 0 ? void 0 : starknetWindowInstance.name) === this.name;
    }
    supportsNetworkSwitching() {
        return false;
    }
    switchNetwork() {
        throw new DynamicError('switchNetwork - Not supported');
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.getWallet();
            return wallet ? [wallet.account.address] : [];
        });
    }
    setupEventListeners(listeners) {
        const wallet = this.getStarknetWindowObject();
        this.handleAccountChange = (address) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!address) {
                yield ((_a = listeners.onDisconnect) === null || _a === void 0 ? void 0 : _a.call(listeners));
                return;
            }
            if (address.toString()) {
                yield ((_b = listeners.onAccountChange) === null || _b === void 0 ? void 0 : _b.call(listeners, [address.toString()]));
            }
        });
        this.handleNetworkChange = (network) => __awaiter(this, void 0, void 0, function* () {
            var _c, _d;
            if (!network) {
                yield ((_c = listeners.onDisconnect) === null || _c === void 0 ? void 0 : _c.call(listeners));
                return;
            }
            if (network.toString()) {
                yield ((_d = listeners.onChainChange) === null || _d === void 0 ? void 0 : _d.call(listeners, network.toString()));
            }
        });
        wallet === null || wallet === void 0 ? void 0 : wallet.on(ACCOUNT_CHANGED_EVENT_LISTENER, this.handleAccountChange);
        wallet === null || wallet === void 0 ? void 0 : wallet.on(NETWORK_CHANGED_EVENT_LISTENER, this.handleNetworkChange);
    }
    teardownEventListeners() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = yield this.getWallet();
            if (this.handleAccountChange) {
                wallet === null || wallet === void 0 ? void 0 : wallet.off(ACCOUNT_CHANGED_EVENT_LISTENER, this.handleAccountChange);
                this.handleAccountChange = undefined;
            }
            if (this.handleNetworkChange) {
                wallet === null || wallet === void 0 ? void 0 : wallet.off(NETWORK_CHANGED_EVENT_LISTENER, this.handleNetworkChange);
                this.handleNetworkChange = undefined;
            }
        });
    }
    getDeepLink() {
        return undefined;
    }
}

export { StarknetProvider as default };
